import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck2, ArrowRight, Loader2, CheckCircle2, AlertCircle, Sparkles, CalendarDays } from "lucide-react";
import Seo, { faqSchema } from "../components/Seo";
import BookCallButton from "../components/BookCallButton";
import { publicApi } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const input = "w-full rounded-xl border border-slate-200 px-4 py-3 text-navy focus:outline-none focus:ring-2 focus:ring-orange/40";

const MTD_FAQS = [
  {
    question: "Who needs to comply with MTD for Income Tax?",
    answer:
      "Sole traders and landlords whose combined qualifying income is at or above the threshold for their wave. The checker above tells you which wave you are in from your own figures, and the thresholds step down over the following years — so being outside it today does not mean being outside it for good.",
  },
  {
    question: "Can you submit my quarterly updates?",
    answer:
      "Yes. We prepare and submit all four quarterly updates and the final declaration, from records we are already keeping current. It forms part of an ongoing bookkeeping and support arrangement rather than a filing service on its own — quarterly deadlines are unforgiving if the records are three months behind.",
  },
  {
    question: "Do I have to use Xero for this?",
    answer:
      "In practice, yes — CASS is a 100% Xero practice and a Xero Platinum Partner. MTD requires digital records with a genuine digital link to HMRC, and keeping every client on one platform is how we make sure that link is real rather than patched together each quarter. If you are moving across, we handle the migration and bring the history with it.",
  },
  {
    question: "I already file a Self Assessment return. Does that stop?",
    answer:
      "Once MTD for Income Tax applies to you, the quarterly updates plus a final declaration replace the Self Assessment return you file today. The information is similar; the timetable is not, which is the part most people need help with.",
  },
  {
    question: "What does it cost?",
    answer:
      "It forms part of your fixed monthly fee rather than appearing as a separate charge. Build your package on the pricing page and you will see your own figure in about two minutes, with no email address needed.",
  },
];


export default function MtdChecker() {
  const [cfg, setCfg] = useState(null);
  const [income, setIncome] = useState("");
  const [se, setSe] = useState(false);
  const [prop, setProp] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");
  const resultRef = useRef(null);

  useEffect(() => { publicApi.mtdConfig().then(setCfg).catch(() => {}); window.scrollTo(0, 0); }, []);

  const submit = async () => {
    setError("");
    const amt = parseFloat(String(income).replace(/[,£\s]/g, ""));
    if (!amt || amt <= 0) { setError("Please enter your combined annual income."); return; }
    if (!se && !prop) { setError("Please tell us whether you have self-employment or property income."); return; }
    setBusy(true);
    try {
      const data = await publicApi.mtdCheck({
        income: amt, has_self_employment: se, has_property: prop,
        name: lead.name, email: lead.email || undefined,
        marketing_consent: lead.marketing_consent, want_ai: true, company_website: hp,
      });
      setRes(data);
      trackEvent("mtd_check", { mandated: data?.result?.mandated });
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      setError(e?.response?.status === 429 ? "Too many attempts — please try again shortly." : "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;
  const mandated = res?.result?.mandated;

  return (
    <div data-testid="mtd-checker-page">
      <Seo title="MTD for Income Tax — Checker and Support for Sole Traders and Landlords"
        description="Check whether Making Tax Digital for Income Tax applies to you and when, then see how CASS handles the setup, the digital records and the quarterly updates for sole traders and landlords."
        jsonLd={[faqSchema(MTD_FAQS)]} />

      <section className="bg-navy-deep text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6"><FileCheck2 size={14} /> Making Tax Digital</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">{cfg.title}</h1>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">{cfg.intro}</p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-7 md:p-10">
            <h2 className="font-display font-extrabold text-2xl text-navy mb-6">Check in 20 seconds</h2>

            <label className="block text-sm font-semibold text-navy mb-2">Your combined annual income (gross, before expenses)</label>
            <div className="relative mb-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">£</span>
              <input data-testid="mtd-income" inputMode="numeric" value={income} onChange={(e) => setIncome(e.target.value)} placeholder="e.g. 55,000" className={`${input} pl-8`} />
            </div>
            <p className="text-xs text-slate-400 mb-6">Total turnover/rental income from self-employment and property combined — not profit.</p>

            <p className="text-sm font-semibold text-navy mb-3">This income comes from… (tick all that apply)</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <button type="button" data-testid="mtd-self-employment" onClick={() => setSe((v) => !v)} className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-colors ${se ? "border-orange bg-orange/5" : "border-slate-200 hover:border-slate-300"}`}>
                <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${se ? "bg-orange text-white" : "border border-slate-300"}`}>{se && <CheckCircle2 size={16} />}</span>
                <span className="text-sm font-semibold text-navy">Self-employment (sole trader)</span>
              </button>
              <button type="button" data-testid="mtd-property" onClick={() => setProp((v) => !v)} className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-left transition-colors ${prop ? "border-orange bg-orange/5" : "border-slate-200 hover:border-slate-300"}`}>
                <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${prop ? "bg-orange text-white" : "border border-slate-300"}`}>{prop && <CheckCircle2 size={16} />}</span>
                <span className="text-sm font-semibold text-navy">Property / rental income</span>
              </button>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <p className="font-display font-bold text-navy">{cfg.lead_heading}</p>
              <p className="text-sm text-slate-500 mt-0.5 mb-4">{cfg.lead_body}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="mtd-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name (optional)" className={input} />
                <input data-testid="mtd-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email (optional — to get your result)" className={input} />
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer mt-3">
                <input type="checkbox" data-testid="mtd-consent" checked={lead.marketing_consent} onChange={(e) => setLead({ ...lead, marketing_consent: e.target.checked })} className="mt-0.5 w-4 h-4 accent-orange" />
                <span>I'd like occasional practical tax & business tips from CASS. Unsubscribe any time.</span>
              </label>
              <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
            </div>

            {error && <p data-testid="mtd-error" className="text-sm text-red-600 mt-4">{error}</p>}

            <button onClick={submit} disabled={busy} data-testid="mtd-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />} {busy ? "Checking…" : "Check my MTD status"}
            </button>
          </div>

          {res && (
            <div ref={resultRef} data-testid="mtd-result" className="bg-white rounded-3xl border border-navy/5 shadow-sm p-7 md:p-10 mt-8">
              <div className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-1.5 rounded-full mb-5 ${mandated ? "bg-orange/10 text-orange" : "bg-teal/10 text-teal"}`}>
                {mandated ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />} {mandated ? "You're affected" : "Not currently mandated"}
              </div>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy leading-snug">{res.result.headline}</h2>
              {mandated && (
                <div className="flex items-center gap-3 mt-4 bg-slate-50 rounded-2xl p-4">
                  <CalendarDays className="text-orange shrink-0" size={22} />
                  <p className="text-navy font-semibold">You'll need to comply from <span className="text-orange">{res.result.from_date}</span> (tax year {res.result.tax_year}).</p>
                </div>
              )}
              {res.summary && (
                <div className="mt-6 border-l-4 border-orange/40 pl-4">
                  <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-orange mb-2"><Sparkles size={13} /> What this means for you</p>
                  <p className="text-slate-600 leading-relaxed">{res.summary}</p>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-6 leading-relaxed">{res.disclaimer}</p>
              <Link to={cfg.cta_link || "/contact"} data-testid="mtd-cta" className="mt-6 inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
                {cfg.cta_label || "Talk to CASS about MTD"} <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Who files it for you — the service, folded in from /services/mtd-for-it
          (consolidated 26 October 2026). The checker answers "am I affected?";
          this answers "who does it". */}
      <section className="py-16 md:py-20" data-testid="mtd-support">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy leading-tight">Who files it for you</h2>
          <p className="text-slate-600 text-lg leading-relaxed mt-5 max-w-3xl">
            Knowing you are affected is the easy part. From the date it applies, you need records kept
            digitally, software that talks to HMRC, a quarterly update filed four times a year and a final
            declaration after the year end. That is what we do for clients, and it is not sold as a separate
            compliance product — it sits inside ongoing bookkeeping and support, because quarterly filing only
            works if the records behind it are already current.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-9">
            {[
              ["Setup and registration", "We confirm which of your income counts towards the threshold, get you signed up with HMRC and make sure the right authorisations are in place before the first quarter starts."],
              ["Digital records that qualify", "Records kept in Xero rather than on a spreadsheet, so the digital link HMRC requires is real rather than retrofitted at the deadline."],
              ["Quarterly updates", "Prepared and submitted for you, four times a year, from records we are already keeping up to date."],
              ["Xero set up for it", "As a Xero Platinum Partner and a 100% Xero practice, we set the software up properly — bank feeds, receipt capture and the property or trade split where you have both."],
              ["The final declaration", "The year-end position pulled together, reliefs and allowances applied, and the submission that replaces the old Self Assessment return."],
              ["Someone to ask", "A named client manager who knows your situation, rather than a helpline. Questions are included in the fee."],
            ].map(([title, body]) => (
              <div key={title} className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
                <h3 className="font-display font-bold text-navy">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{body}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-10">
            <BookCallButton testId="mtd-book-call" label="Request a Discovery Call"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5" />
            <Link to="/pricing" data-testid="mtd-pricing"
              className="inline-flex items-center gap-2 border-2 border-navy/15 hover:border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-colors">
              Build Your Indicative Price <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-20" data-testid="mtd-faqs">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy">Common questions</h2>
          <div className="mt-6 space-y-3">
            {MTD_FAQS.map((f, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl border border-slate-200 p-6" data-testid={`mtd-faq-${i}`}>
                <h3 className="font-display font-bold text-navy">{f.question}</h3>
                <p className="text-slate-600 leading-relaxed mt-2">{f.answer}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-8">
            Already using Xero and want the records side handled properly?{" "}
            <Link to="/services/bookkeeping" className="text-orange font-semibold hover:underline">See our bookkeeping service</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
